import { ColumnDef, flexRenderComponent } from '@tanstack/angular-table';
import { TableAmountCellComponent } from '@core/components/table/table-amount-cell/table-amount-cell.component';
import { TableColorCellComponent } from '@core/components/table/table-color-cell/table-color-cell.component';
import { TableDateCellComponent } from '@core/components/table/table-date-cell/table-date-cell.component';
import { PlayerRowComponent } from '@core/components/player-row/player-row.component';
import { ClipboardService } from '@core/services/clipboard.service';
import { AdminAffiliateTrackerEvent } from './admin-affiliate-tracker.interface';

interface Props {
  copy: typeof ClipboardService.prototype.copy;
}

// BE may send unix seconds or milliseconds — normalize to ms for DatePipe
const toMs = (time: number | undefined): number | undefined =>
  time ? (time > 1e12 ? time : time * 1000) : undefined;

const shortenId = (id: string): string =>
  id && id.length > 16 ? `${id.slice(0, 10)}…${id.slice(-4)}` : id;

export const adminAffiliateTrackerTableConfig = ({
  copy,
}: Props): ColumnDef<AdminAffiliateTrackerEvent>[] => [
  {
    accessorKey: 'name',
    id: 'name',
    header: ({ column }) => ({
      content: 'Player',
      onClick: () => column.toggleSorting(),
    }),
    cell: (cell) => {
      const event = cell.row.original;
      return flexRenderComponent(PlayerRowComponent, {
        inputs: {
          player: {
            // BE doesn't join profiles into tracker events — name/avatar arrive null
            name: event.name ?? event.userid,
            avatar: event.avatar,
            steamid: event.userid,
            level: event.level,
          },
        },
      });
    },
    enableSorting: true,
  },
  {
    accessorFn: (row) => toMs(row.time_stamp),
    id: 'time_stamp',
    header: ({ column }) => ({
      content: 'Time',
      onClick: () => column.toggleSorting(),
    }),
    cell: (info) =>
      flexRenderComponent(TableDateCellComponent, {
        inputs: { value: info.getValue() as number },
      }),
    enableSorting: true,
  },
  {
    accessorKey: 'type',
    id: 'type',
    header: ({ column }) => ({
      content: 'Type',
      onClick: () => column.toggleSorting(),
    }),
    cell: (info) => info.getValue(),
    enableSorting: true,
  },
  {
    accessorKey: 'source',
    id: 'source',
    header: ({ column }) => ({
      content: 'Source',
      onClick: () => column.toggleSorting(),
    }),
    cell: (info) => info.getValue(),
    enableSorting: true,
  },
  {
    accessorKey: 'source_id',
    id: 'source_id',
    header: ({ column }) => ({
      content: 'Source ID',
      onClick: () => column.toggleSorting(),
    }),
    cell: (info) => {
      const sourceId = (info.getValue() as string) ?? '';
      return flexRenderComponent(TableColorCellComponent, {
        inputs: {
          value: shortenId(sourceId),
          color: 'var(--gray)',
          cursor: 'pointer',
        },
        outputs: {
          handleClick: () => {
            copy(sourceId, 'Source ID copied!');
          },
        },
      });
    },
    enableSorting: true,
  },
  {
    accessorKey: 'amount',
    id: 'amount',
    header: ({ column }) => ({
      content: 'Amount',
      onClick: () => column.toggleSorting(),
    }),
    cell: (info) =>
      flexRenderComponent(TableAmountCellComponent, {
        inputs: {
          value: info.getValue() as number,
          color: 'var(--yellow)',
        },
      }),
    enableSorting: true,
  },
  {
    accessorKey: 'wager',
    id: 'wager',
    header: ({ column }) => ({
      content: 'Wager',
      onClick: () => column.toggleSorting(),
    }),
    cell: (info) =>
      flexRenderComponent(TableAmountCellComponent, {
        inputs: {
          value: info.getValue() as number,
          color: 'var(--ocean)',
        },
      }),
    enableSorting: true,
  },
  {
    accessorKey: 'earned',
    id: 'earned',
    header: ({ column }) => ({
      content: 'Earned',
      onClick: () => column.toggleSorting(),
    }),
    cell: (info) =>
      flexRenderComponent(TableAmountCellComponent, {
        inputs: { value: info.getValue() as number },
      }),
    enableSorting: true,
  },
];
